export default class DateUtils {
  private static readonly SAO_PAULO_OFFSET = '-03:00';
  private static readonly TIMEZONE_SUFFIX = /([zZ]|[+-]\d{2}:?\d{2})$/;

  public static getCurrentDate(): string {
    return DateUtils.formatDate(new Date());
  }

  public static getStartOfLastMonth(): string {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() - 1);
    return DateUtils.formatDate(date);
  }

  public static getEndOfLastMonth(): string {
    const date = new Date();
    date.setDate(0);
    return DateUtils.formatDate(date);
  }

  private static formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  public static isValidDate(dateStr: string): boolean {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      return false;
    }

    const [day, month, year] = dateStr.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  }

  public static isTodayOrFuture(dateStr: string): boolean {
    const [day, month, year] = dateStr.split('/').map(Number);
    const inputDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    inputDate.setHours(0, 0, 0, 0);
    return inputDate >= today;
  }

  public static formatYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  /**
   * Serializa um `Date`/string em `YYYY-MM-DDTHH:mm:ss` preservando o
   * wall-clock LOCAL, sem passar por `toISOString()`/UTC.
   *
   * O caminho de produção (filtro de período do DDA) sempre recebe um `Date`
   * construído em hora local — ex.: `new Date(ano, mes, dia, 23, 59, 59)` para
   * o fim do período. Usar `toISOString()` converteria para UTC e, no fuso do
   * Brasil (UTC-3), deslocaria a data (`23/07 23:59:59` local viraria
   * `2026-07-24T02:59:59`), vazando títulos do dia seguinte no filtro
   * inclusivo (`expireAt <= expire_at_end`). Ver card #169413.
   *
   * @param date `Date` local (uso real) ou string parseável por `new Date`.
   *   Atenção: `new Date("2026-07-23")` (string date-only) é interpretado como
   *   UTC pelo runtime, então o wall-clock resultante depende do fuso; o
   *   contrato aqui é preservar os componentes locais do `Date` produzido.
   * @returns data/hora local no formato `YYYY-MM-DDTHH:mm:ss`, ou `''` para
   *   entrada inválida.
   */
  public static toIsoDateTime(date: string | Date): string {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      + `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  private static readonly SUBSECOND_OVERFLOW = /(\.\d{3})\d+/;

  public static parseServerDateTime(value?: string | null): number {
    if (!value) {
      return NaN;
    }
    const trimmed = value.replace(DateUtils.SUBSECOND_OVERFLOW, '$1');
    const normalized = DateUtils.TIMEZONE_SUFFIX.test(trimmed) ? trimmed : `${trimmed}${DateUtils.SAO_PAULO_OFFSET}`;
    return Date.parse(normalized);
  }

  public static formatBRDate(isoDate?: string): string {
    const date = DateUtils.parseIsoDate(isoDate);
    return date ? DateUtils.formatDate(date) : '';
  }

  public static formatLongMonthDate(isoDate?: string, locale: string = 'pt-BR'): string {
    const date = DateUtils.parseIsoDate(isoDate);
    if (!date) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const monthRaw = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date).replace('.', '');
    const month = monthRaw.charAt(0).toUpperCase() + monthRaw.slice(1);
    return `${day} ${month} ${date.getFullYear()}`;
  }

  public static formatShortMonthDate(isoDate?: string, locale: string = 'pt-BR', includeYear = false): string {
    const date = DateUtils.parseIsoDate(isoDate);
    if (!date) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date).replace('.', '').toLowerCase();
    if (!includeYear) {
      return `${day}/${month}`;
    }
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  }

  public static monthsBetween(isoDateA?: string, isoDateB?: string): number {
    const dateA = DateUtils.parseIsoDate(isoDateA);
    const dateB = DateUtils.parseIsoDate(isoDateB);
    if (!dateA || !dateB) {
      return 0;
    }
    const [start, end] = dateA.getTime() <= dateB.getTime() ? [dateA, dateB] : [dateB, dateA];
    let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (end.getDate() < start.getDate()) {
      months -= 1;
    }
    return Math.max(months, 0);
  }

  public static spansMoreThanOneYear(isoDates: Array<string | undefined>): boolean {
    const valid = isoDates.filter((value): value is string => !!value);
    if (valid.length < 2) {
      return false;
    }
    const first = valid[0];
    const last = valid[valid.length - 1];
    return DateUtils.monthsBetween(first, last) > 12;
  }

  public static formatShortDateTime(isoDateTime?: string): string {
    if (!isoDateTime) {
      return '';
    }
    const date = new Date(isoDateTime);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month} ${hours}:${minutes}`;
  }

  private static parseIsoDate(isoDate?: string): Date | null {
    if (!isoDate) {
      return null;
    }
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }
}
